// The per-card "Copy" control. A single trigger opens a preview modal where
// you pick a format — Raw, Slack, Email, or Teams — see it rendered, and
// copy. Email/Teams copy as RICH TEXT (pastes formatted into Gmail/Outlook/
// Teams); Raw/Slack copy as plain text. See lib/exportHtml.ts.
import { useEffect, useMemo, useState } from "react";
import { Copy } from "lucide-react";
import { Button } from "@/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/ui/dialog";
import { cn } from "@/lib/cn";
import { copyHtml, copyPlain } from "@/lib/copy";
import {
  buildExport,
  type Composed,
  type ExportFormat,
} from "@/lib/exportHtml";
import { useTrack } from "@/lib/track";

const FORMATS: { key: ExportFormat; label: string; copyLabel: string }[] = [
  { key: "raw", label: "Raw", copyLabel: "Copy raw text" },
  { key: "slack", label: "Slack", copyLabel: "Copy for Slack" },
  { key: "email", label: "Email", copyLabel: "Copy email" },
  { key: "teams", label: "Teams", copyLabel: "Copy for MSFT Teams" },
];

/** Single "Copy" chip + the shared preview modal. Stops event propagation so
 *  clicking inside a <summary> doesn't toggle the surrounding <details>. */
export function CopyExportButtons({ composed }: { composed: Composed }) {
  const [open, setOpen] = useState(false);
  const [format, setFormat] = useState<ExportFormat>("raw");

  return (
    <>
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen(true);
        }}
        className="inline-flex items-center gap-1.5 rounded-md border border-text/30 bg-card px-2.5 py-1 text-xs font-medium text-text shadow-sm transition-colors hover:border-text hover:bg-text hover:text-bg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/50"
        title="Copy as raw text, Slack, email, or Teams"
      >
        <Copy className="h-3.5 w-3.5" />
        Copy
      </button>
      <CopyFormatModal
        open={open}
        onOpenChange={setOpen}
        composed={composed}
        format={format}
        onFormatChange={setFormat}
      />
    </>
  );
}

function CopyFormatModal({
  open,
  onOpenChange,
  composed,
  format,
  onFormatChange,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  composed: Composed;
  format: ExportFormat;
  onFormatChange: (f: ExportFormat) => void;
}) {
  const [copied, setCopied] = useState(false);
  const [err, setErr] = useState("");
  const track = useTrack();

  const payload = useMemo(
    () => buildExport(composed, format),
    [composed, format],
  );

  // HTML previews render in a sandboxed iframe: no script execution, and the
  // email's inline styles stay isolated from the app's stylesheet.
  const srcDoc = useMemo(
    () =>
      payload.kind === "html" && payload.html
        ? `<!doctype html><html><head><meta charset="utf-8"><style>body{margin:0;padding:20px;background:#fff;color:#171717;font-family:Arial,Helvetica,sans-serif;font-size:14px}</style></head><body>${payload.html}</body></html>`
        : "",
    [payload],
  );

  // Reset copy feedback whenever the modal reopens or the format flips.
  useEffect(() => {
    setCopied(false);
    setErr("");
  }, [open, format]);

  const doCopy = async () => {
    setErr("");
    try {
      if (payload.kind === "html" && payload.html) {
        await copyHtml(payload.html, payload.plain);
      } else {
        await copyPlain(payload.plain);
      }
      setCopied(true);
      track({
        event: "export",
        meta: { format, bytes: new Blob([payload.plain]).size },
      });
      window.setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  };

  const meta = FORMATS.find((f) => f.key === format)!;
  const hasChrome = format === "email" || format === "teams";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Copy</DialogTitle>
          <DialogDescription>
            Email and Teams paste as formatted text; Raw and Slack copy as
            plain text.
            {hasChrome && (
              <>
                {" "}
                The{" "}
                <code className="rounded bg-border/50 px-1">[First name]</code>{" "}
                placeholder is intentional — fill it in after pasting.
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        {/* Format picker */}
        <div className="inline-flex w-fit rounded-md border border-border p-0.5">
          {FORMATS.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => onFormatChange(f.key)}
              className={cn(
                "rounded px-3 py-1 text-sm",
                format === f.key
                  ? "bg-border text-text"
                  : "text-text-secondary hover:text-text",
              )}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Subject line — email only; not part of the copied body (the
            clipboard can't set a mail subject), shown for convenience. */}
        {format === "email" && (
          <div className="text-xs text-text-tertiary">
            Subject:{" "}
            <span className="text-text-secondary">{payload.subject}</span>
          </div>
        )}

        {payload.kind === "html" ? (
          <iframe
            title={`${meta.label} preview`}
            sandbox=""
            srcDoc={srcDoc}
            className="h-[42vh] w-full rounded-md border border-border bg-white"
          />
        ) : (
          <pre className="h-[42vh] w-full overflow-auto whitespace-pre-wrap rounded-md border border-border bg-bg p-4 font-mono text-xs leading-snug text-text">
            {payload.plain}
          </pre>
        )}

        {err && <p className="text-xs text-destructive">{err}</p>}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          <Button onClick={doCopy}>{copied ? "Copied ✓" : meta.copyLabel}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
